import { useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTour } from '@/context/TourContext';
import Calendar, { CalendarHandle } from '@/components/Calendar';
import DateRangeSearch from '@/components/DateRangeSearch';

const TOUR_COMPLETED_KEY = 'tour_completed';

// Check localStorage as backup (in case server state hasn't synced yet)
function isTourCompletedLocally(): boolean {
  try {
    return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const calendarRef = useRef<CalendarHandle>(null);
  const tourStartedRef = useRef(false);

  // Auto-start tour for first-time users
  useEffect(() => {
    // Check both server state AND localStorage backup
    const tourCompleted = user?.tour_completed || isTourCompletedLocally();
    if (user && !tourCompleted && !tourStartedRef.current) {
      tourStartedRef.current = true;
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, startTour]);

  const handleNavigateToDate = (date: string) => {
    calendarRef.current?.goToDate(date);
  };

  // Role-aware welcome message content
  const getWelcomeMessage = () => {
    if (!user) return null;

    if (user.role === 'owner') {
      return {
        title: 'Welcome to Apartment 5H Calendar!',
        description: 'As an owner, your bookings are automatically approved. Check the Admin menu to manage requests from other users.',
      };
    }

    if (user.role === 'admin') {
      return {
        title: 'Welcome to Apartment 5H Calendar!',
        description: 'As an admin, you can manage users and content. Booking requests still require owner approval.',
      };
    }

    return {
      title: 'Welcome to Apartment 5H Calendar!',
      description: 'Click and drag on dates to request a stay. Your requests will be reviewed by the owner.',
    };
  };

  const welcomeMessage = getWelcomeMessage();
  const showWelcomeBanner = user && !user.tour_completed && !isTourCompletedLocally() && welcomeMessage;

  return (
    <div className="space-y-6">
      {/* Welcome message for first-time users */}
      {showWelcomeBanner && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="font-medium text-primary-900">
                {welcomeMessage.title}
              </h2>
              <p className="mt-1 text-sm text-primary-700">
                {welcomeMessage.description}
              </p>
            </div>
            <button
              onClick={startTour}
              className="text-primary-600 hover:text-primary-800 text-sm font-medium whitespace-nowrap"
            >
              Take Tour
            </button>
          </div>
        </div>
      )}

      {/* Date Range Search */}
      <DateRangeSearch onNavigateToDate={handleNavigateToDate} />

      {/* Calendar */}
      <Calendar ref={calendarRef} />
    </div>
  );
}
